import React from 'react';
import { FormattedMessage } from 'react-intl';
import Portal from '../ui/portal';
import { DrawerModalOverlaySC, DrawerModalSC } from './drawer-modal.styled';
import { CSSTransition } from 'react-transition-group';

type TProps = {
  visible: boolean;
  onClose(): void;
  children: React.ReactNode;
  closeTheme?: 'DARK' | 'LIGHT';
  backStyle?: React.CSSProperties;
  showOverlay?: boolean;
  unmountOnExit?: boolean;
  /**
   * If we can close modal by clicking on backdrop/esc
   */
  closable?: boolean;
};

const DrawerModal: React.ForwardRefRenderFunction<HTMLDivElement, TProps> = (
  {
    visible,
    onClose,
    children,
    closeTheme = 'DARK',
    backStyle,
    showOverlay,
    unmountOnExit = true,
    closable = true,
  },
  ref,
) => {
  /**
   * Add/remove class to body when wing hiding modal.
   * Exception is when we are nested.
   * Notify parent on change when nested
   */
  React.useEffect(() => {
    if (visible && showOverlay) {
      document.body.classList.add('body-drawer-open');
    } else {
      document.body.classList.remove('body-drawer-open');
    }
  }, [showOverlay, visible]);

  React.useEffect(() => {
    function handleEsc(event: React.KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose?.();
      }
    }
    if (visible && closable) {
      // @ts-ignore
      document.addEventListener('keydown', handleEsc, false);
    }
    return () => {
      // @ts-ignore
      document.removeEventListener('keydown', handleEsc, false);
    };
  }, [onClose, visible, closable]);

  return (
    <Portal>
      <CSSTransition
        in={visible}
        timeout={300}
        classNames="drawer-modal"
        unmountOnExit={unmountOnExit}
        appear
      >
        <div style={{ position: 'relative' }}>
          {showOverlay && <DrawerModalOverlaySC onClick={closable ? onClose : undefined} />}
          <DrawerModalSC closeTheme={closeTheme} ref={ref}>
            <div className="dm-back" onClick={onClose} style={backStyle}>
              <FormattedMessage id="questionnaire.back" />
              {closeTheme === 'LIGHT' ? (
                <img src={require('../../assets/svg/icon-chevron-left-white.svg').default} alt="" />
              ) : (
                <img src={require('../../assets/svg/icon-chevron-left.svg').default} alt="" />
              )}
            </div>
            {children}
          </DrawerModalSC>
        </div>
      </CSSTransition>
    </Portal>
  );
};

export default React.forwardRef(DrawerModal);
